import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Stack from "react-bootstrap/Stack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCircleCheck,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";

import { MainLayout } from "../layouts/mainLayout";
import { useApiClient } from "../helpers/useApiClient";
import { formatPostcode } from "../helpers/postcodeHelper";
import { LocationViewModel, ReportLocationBindingModel, ValidationProblemDetails } from "../apiClient";
import { ApiError } from "../components/apiError";
import { RecaptchaDisclaimer } from "../components/recaptchaDisclaimer";

type ReportRouteParams = {
  id: string;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Please enter your name"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter your email address so we can follow up"),
  message: Yup.string().required("Please describe the issue with this retailer"),
});

export const Report = () => {
  const apiClient = useApiClient();
  const { id: locationId } = useParams<ReportRouteParams>();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<Error | ValidationProblemDetails | null>(null);
  const [location, setLocation] = useState<LocationViewModel | null>(null);
  const [loading, setLoading] = useState(true);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema,
    onSubmit: async () => {
      setError(null);
      setSuccess(false);
      try {
        await apiClient.report(
          location!.id!,
          new ReportLocationBindingModel({
            name: formik.values.name,
            email: formik.values.email,
            message: formik.values.message,
            captcha: executeRecaptcha ? await executeRecaptcha("report") : undefined,
          })
        );

        setSuccess(true);
        formik.resetForm();
      } catch (err) {
        console.error(err);
        if (err instanceof ValidationProblemDetails || err instanceof Error) {
          setError(err);
        }
      }
    },
  });

  useEffect(() => {
    const loadData = async () => {
      if (!locationId) return;
      setLoading(true);
      try {
        const locationData = await apiClient.locationsGET(locationId);
        setLocation(locationData);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err : new Error("Could not load location"));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [apiClient, locationId]);

  const returnUrl = location?.postcode ? `/results/${location.postcode}` : "/";

  return (
    <MainLayout loading={loading || formik.isSubmitting} title="Report Retailer Issue">
      <Container className="py-4" style={{ maxWidth: 740 }}>
        <Link to={returnUrl} className="kf-back-link">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to results</span>
        </Link>

        <ApiError error={error} />

        {success ? (
          <div className="kf-form-card text-center py-5">
            <div className="text-success mb-3" style={{ fontSize: "3rem" }}>
              <FontAwesomeIcon icon={faCircleCheck} />
            </div>
            <h1 className="h2 mb-3">Thank you for reporting!</h1>
            <p className="text-secondary mb-4" style={{ maxWidth: 500, margin: "0 auto" }}>
              We have received your report regarding <strong>{location?.name}</strong>. Our moderators will review the details promptly.
            </p>
            <Link to={returnUrl} className="btn btn-primary d-inline-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to results</span>
            </Link>
          </div>
        ) : (
          <div className="kf-form-card">
            <h1 className="h2 mb-1">Report Location Issue</h1>
            {location && (
              <p className="text-secondary mb-4">
                Reporting issue for <strong className="text-light">{location.name}</strong> ({formatPostcode(location.postcode || "")})
              </p>
            )}

            <Form onSubmit={formik.handleSubmit}>
              <Stack gap={4}>
                <Form.Group controlId="name">
                  <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your name"
                    name="name"
                    required
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="email">
                  <Form.Label>Your Email Address <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    name="email"
                    required
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!(formik.touched.email && formik.errors.email)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="message">
                  <Form.Label>What is the issue? <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="e.g. This store no longer stocks Blade kegs / The store has permanently closed / Postcode is incorrect..."
                    name="message"
                    required
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!(formik.touched.message && formik.errors.message)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <RecaptchaDisclaimer />

                <div>
                  <Button
                    variant="danger"
                    type="submit"
                    size="lg"
                    className="d-inline-flex align-items-center gap-2 fw-semibold"
                    style={{ borderRadius: "0.75rem", padding: "0.85rem 2rem" }}
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span>Submit Report</span>
                  </Button>
                </div>
              </Stack>
            </Form>
          </div>
        )}
      </Container>
    </MainLayout>
  );
};